import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  FileText, Target, Users, Calendar, TrendingUp, 
  AlertTriangle, CheckCircle, Clock, DollarSign 
} from 'lucide-react'
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import api from '../lib/api'

const Dashboard = () => {
  const [stats, setStats] = useState(null)
  const [activity, setActivity] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      const [statsRes, activityRes] = await Promise.all([
        api.get('/dashboard/overview'),
        api.get('/dashboard/activity')
      ])
      setStats(statsRes.data)
      setActivity(activityRes.data)
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  const statCards = [
    {
      name: 'Active Tenders',
      value: stats?.tenders?.active || 0,
      change: `+${stats?.tenders?.thisMonth || 0} this month`,
      icon: FileText,
      color: 'bg-blue-500',
      link: '/tenders'
    },
    {
      name: 'Active Proposals',
      value: stats?.proposals?.active || 0,
      change: `₹${((stats?.proposals?.cumulativeValue || 0) / 10000000).toFixed(2)} Cr value`,
      icon: Target,
      color: 'bg-green-500',
      link: '/proposals'
    },
    {
      name: 'Qualified Leads',
      value: stats?.leads?.qualified || 0,
      change: `${stats?.leads?.conversionRate || 0}% conversion`,
      icon: Users,
      color: 'bg-purple-500',
      link: '/leads'
    },
    {
      name: 'Upcoming Events',
      value: stats?.events?.upcoming || 0,
      change: `${stats?.events?.completed || 0} completed`,
      icon: Calendar,
      color: 'bg-orange-500',
      link: '/events'
    }
  ]

  const pipelineData = [
    { name: 'Pipeline Value', value: stats?.pipeline?.value || 0 },
    { name: 'Won Value', value: stats?.pipeline?.wonValue || 0 }
  ]

  const COLORS = ['#3b82f6', '#10b981']

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Welcome back! Here's your BD performance overview.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <Link
            key={stat.name}
            to={stat.link}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stat.value}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{stat.name}</p>
            <p className="text-xs text-green-600 dark:text-green-400 mt-2">{stat.change}</p>
          </Link>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Value */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Pipeline Value</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={pipelineData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => `₹${(value / 1000000).toFixed(2)}M`} />
              <Bar dataKey="value" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Win Rate */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Proposal Performance</h3>
          <div className="flex items-center justify-center h-[300px]">
            <div className="text-center">
              <div className="text-5xl font-bold text-primary-600 dark:text-primary-400 mb-2">
                {stats?.proposals?.winRate || 0}%
              </div>
              <p className="text-gray-600 dark:text-gray-400">Win Rate</p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-center text-sm text-gray-700 dark:text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  <span>{stats?.proposals?.won || 0} Won</span>
                </div>
                <div className="flex items-center justify-center text-sm text-gray-700 dark:text-gray-300">
                  <Clock className="w-4 h-4 text-yellow-500 mr-2" />
                  <span>{stats?.proposals?.submitted || 0} Submitted</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Tenders */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Recent Tenders</h3>
          <div className="space-y-3">
            {activity?.tenders?.slice(0, 5).map((tender) => (
              <div key={tender._id} className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 mt-2 bg-blue-500 rounded-full"></div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
                    {tender.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {tender.aiRelevanceScore}% relevance
                  </p>
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/tenders"
            className="block mt-4 text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            View all tenders →
          </Link>
        </div>

        {/* Recent Proposals */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Recent Proposals</h3>
          <div className="space-y-3">
            {activity?.proposals?.slice(0, 5).map((proposal) => (
              <div key={proposal._id} className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 mt-2 bg-green-500 rounded-full"></div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
                    {proposal.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {proposal.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/proposals"
            className="block mt-4 text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            View all proposals →
          </Link>
        </div>

        {/* Recent Leads */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Recent Leads</h3>
          <div className="space-y-3">
            {activity?.leads?.slice(0, 5).map((lead) => (
              <div key={lead._id} className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 mt-2 bg-purple-500 rounded-full"></div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {lead.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {lead.company} · {lead.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/leads"
            className="block mt-4 text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            View all leads →
          </Link>
        </div>
      </div>

      {/* Alerts Section */}
      {stats?.alerts?.active > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6">
          <div className="flex items-center">
            <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-500 mr-3" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200">
                {stats.alerts.active} Active Alert{stats.alerts.active > 1 ? 's' : ''}
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                You have pending alerts that require attention
              </p>
            </div>
            <Link
              to="/alerts"
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
            >
              View Alerts
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard
